import { Reorder } from 'framer-motion';
import { List, Pencil, Trash } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { TabsDeleteDialog } from '@/components/modules/shipment/tabs/delete-dialog';
import { TabsEditDialog } from '@/components/modules/shipment/tabs/edit-dialog';
import { CreateShipmentListTabFormData } from '@/components/modules/shipment/tabs/form';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import { DragItem } from '@/components/ui/drag-item';
import { Drawer } from '@/components/ui/drawer';
import { DrawerHeader } from '@/components/ui/drawer/header';
import { useUserSettings } from '@/hooks/use-user-settings';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { sortBySortOrder } from '@/lib/models/saved-item/utils';
import { tv } from '@/utils/styles';

export interface ShipmentManageFiltersProps {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  savedItems: SavedItemModel[];
  onClickEdit: (item: SavedItemModel) => Promise<any>;
  onClickDelete: (item: SavedItemModel) => Promise<any>;
  onClickItem: (item: SavedItemModel) => void;
  onReorder: (items: SavedItemModel[]) => void;
}

const ShipmentManageFilters = ({
  isOpen,
  onOpenChange,
  savedItems,
  onClickDelete,
  onClickEdit,
  onClickItem,
  onReorder,
}: ShipmentManageFiltersProps) => {
  const { shipmentViewSorting } = useUserSettings();
  const tabSorting = shipmentViewSorting?.value || [];

  const [selectedItem, setSelectedItem] = useState<SavedItemModel>();
  const [action, setAction] = useState<'edit' | 'delete'>();
  const [orderedItems, setOrderedItems] = useState(savedItems);
  const [hasDragged, setHasDragged] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    // Only trigger the onReorder function when the user stops dragging
    if (!isDragging && hasDragged) {
      onReorder(orderedItems);
    }
  }, [isDragging, orderedItems]);

  useEffect(() => {
    // Required to prevent onReorder function firing on first render
    if (isDragging && !hasDragged) {
      setHasDragged(true);
    }
  }, [isDragging]);

  useEffect(() => {
    const sortedTabs = sortBySortOrder(savedItems, tabSorting);
    setOrderedItems(sortedTabs);
  }, [savedItems]);

  const handleClickEdit = (item: SavedItemModel) => {
    setSelectedItem(item);
    setAction('edit');
  };

  const handleCloseModal = () => {
    setAction(undefined);
    setSelectedItem(undefined);
  };

  const handleClickDelete = (item: SavedItemModel) => {
    setSelectedItem(item);
    setAction('delete');
  };

  const handleEdit = async ({ label }: CreateShipmentListTabFormData) => {
    if (!selectedItem) {
      return;
    }

    if (selectedItem.label === label) {
      // Label hasn't changed, just close the modal
      handleCloseModal();
      return;
    }

    const updatedItem: SavedItemModel = {
      ...selectedItem,
      label,
    };

    await onClickEdit(updatedItem);
    handleCloseModal();
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    await onClickDelete(selectedItem);
    handleCloseModal();
  };

  const { base, actionWrapper, actionIcon, dragItemWrapper, dragItemLabel, reorderGroup } = styles();

  return (
    <Drawer width="420px" variant="single" closeDrawer={() => onOpenChange(false)} isOpen={isOpen}>
      <div className={base()}>
        <DrawerHeader title="Saved views" icon={<List size={16} />} onClose={() => onOpenChange(false)} />
        <Text className="text-baseSm">
          Change the order, rename or delete your saved views using the controls below
        </Text>
        <Reorder.Group className={reorderGroup()} axis="y" values={orderedItems} onReorder={setOrderedItems}>
          {orderedItems.map((item: SavedItemModel) => (
            <DragItem
              key={item.id || ''}
              value={item}
              onDragEnd={() => setIsDragging(false)}
              onDragStart={() => setIsDragging(true)}
            >
              <div className={dragItemWrapper()}>
                <button type="button" className={dragItemLabel()} onClick={() => onClickItem(item)}>
                  {item.label}
                </button>
                <div className={actionWrapper()}>
                  <Tooltip content="Rename">
                    <button
                      type="button"
                      aria-label="Edit"
                      className="hover:text-grey-700"
                      onClick={() => handleClickEdit(item)}
                    >
                      <Pencil className={actionIcon()} size={16} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Delete">
                    <button
                      type="button"
                      aria-label="Delete"
                      className="hover:text-errorDark"
                      onClick={() => handleClickDelete(item)}
                    >
                      <Trash className={actionIcon()} size={16} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </DragItem>
          ))}
        </Reorder.Group>
      </div>
      {selectedItem ? (
        <TabsEditDialog
          isOpen={action === 'edit'}
          onOpenChange={handleCloseModal}
          onSubmit={handleEdit}
          savedItem={selectedItem}
        />
      ) : null}

      <TabsDeleteDialog
        item={selectedItem}
        onOpenChange={handleCloseModal}
        isOpen={action === 'delete'}
        onCancel={handleCloseModal}
        onConfirm={handleDelete}
      />
    </Drawer>
  );
};

const styles = tv({
  slots: {
    base: 'p-4',
    reorderGroup: 'flex flex-col gap-1',
    dragItemWrapper: 'flex w-full justify-between gap-2',
    dragItemLabel: 'flex-1 py-2 text-left hover:underline',
    actionWrapper: 'flex items-center text-grey-400',
    actionIcon: 'mr-2 h-4 w-4',
  },
});

export { ShipmentManageFilters };
